import { z } from 'zod';

/**
 * E.164 phone number validation regex
 * - Starts with +
 * - Followed by 7-15 digits (country code + number)
 * - No spaces, dashes, or parentheses
 *
 * Note: Full country-specific validation happens client-side via PhoneInput component.
 * This server-side validation ensures basic E.164 format compliance.
 */
const E164_PHONE_REGEX = /^\+[1-9]\d{6,14}$/;

export const GuestRegistrationSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  // Phone is required for guest registration (primary authentication method)
  phoneNumber: z.string()
    .min(1, 'Phone number is required')
    .refine((value) => {
      if (!value) return false;
      // Server-side E.164 format validation
      return E164_PHONE_REGEX.test(value);
    }, {
      message: 'Please enter a valid phone number'
    }),
  // Email is optional here because the Apple Pay flows reuse this schema and the
  // wallet does not always share an email. The guest-checkout form collects it
  // directly, so it uses GuestCheckoutRegistrationSchema below instead.
  email: z.string().email('Please enter a valid email address').optional().or(z.literal('')),
});

export type GuestRegistrationData = z.infer<typeof GuestRegistrationSchema>;

/**
 * Guest checkout sheet variant: the customer types the email, so it is required.
 * Kept alongside the base schema so the drawer and its server action validate
 * against the same definition.
 */
export const GuestCheckoutRegistrationSchema = GuestRegistrationSchema.extend({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

export type GuestCheckoutRegistrationData = z.infer<
  typeof GuestCheckoutRegistrationSchema
>;
